/**
 * AuthenticationField - Auth type selector with type-specific forms
 *
 * Supports multiple authentication types with appropriate form fields for each:
 * - None
 * - Basic (username/password)
 * - API Key (key, location, name)
 * - OAuth2 (client ID, secret, token URL, scopes)
 * - Client Certificate (PFX, password)
 * - Managed Identity (audience, identity)
 */

import { useState, useCallback, useMemo, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  Label,
  Text,
  Button,
  Dropdown,
  Option,
  Input,
  Tooltip,
} from '@fluentui/react-components';
import {
  CodeFilled,
  TextFieldFilled,
  EyeOffRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { TokenizedInput } from './TokenizedInput';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  modeToggle: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
  },
  typeSelector: {
    marginBottom: tokens.spacingVerticalS,
  },
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  fieldDescription: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  passwordWrapper: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'flex-start',
  },
  passwordInput: {
    flex: 1,
  },
  visibilityButton: {
    minWidth: 'auto',
    height: '32px',
    padding: tokens.spacingHorizontalS,
  },
  noAuthHint: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

// Authentication types
export type AuthType =
  | 'none'
  | 'basic'
  | 'apiKey'
  | 'oauth2'
  | 'clientCertificate'
  | 'managedIdentity';

const AUTH_TYPE_LABELS: Record<AuthType, string> = {
  none: 'None',
  basic: 'Basic',
  apiKey: 'API Key',
  oauth2: 'OAuth 2.0',
  clientCertificate: 'Client Certificate',
  managedIdentity: 'Managed Identity',
};

// API Key locations
type ApiKeyLocation = 'header' | 'query';

// Auth configuration interfaces
interface BaseAuthConfig {
  type: AuthType;
}

interface BasicAuthConfig extends BaseAuthConfig {
  type: 'basic';
  username: string;
  password: string;
}

interface ApiKeyAuthConfig extends BaseAuthConfig {
  type: 'apiKey';
  key: string;
  location: ApiKeyLocation;
  name: string;
}

interface OAuth2AuthConfig extends BaseAuthConfig {
  type: 'oauth2';
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
  scopes: string;
}

interface ClientCertificateAuthConfig extends BaseAuthConfig {
  type: 'clientCertificate';
  pfx: string;
  password: string;
}

interface ManagedIdentityAuthConfig extends BaseAuthConfig {
  type: 'managedIdentity';
  audience: string;
  identity?: string;
}

type AuthConfig =
  | { type: 'none' }
  | BasicAuthConfig
  | ApiKeyAuthConfig
  | OAuth2AuthConfig
  | ClientCertificateAuthConfig
  | ManagedIdentityAuthConfig;

export interface AuthenticationFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Allowed auth types (defaults to all) */
  allowedTypes?: AuthType[] | undefined;
  /** Show the agent parameter button */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs for previews */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since last run */
  isStale?: boolean | undefined;
  /**
   * Optional slot rendered in the label row alongside the visual / JSON
   * mode toggle. Hosts inline affordances such as `FieldActionChips` so
   * they share the same right-aligned area as the toggle instead of
   * floating absolutely on top of it.
   */
  endActions?: ReactNode | undefined;
}

/**
 * Parse auth config string into AuthConfig
 */
function parseAuthConfig(value: string | ValueSegment[]): AuthConfig {
  const stringValue = isValueSegmentArray(value)
    ? segmentsToString(value)
    : value;

  if (!stringValue || stringValue.trim() === '') {
    return { type: 'none' };
  }

  try {
    const parsed = JSON.parse(stringValue);
    return parsed as AuthConfig;
  } catch {
    return { type: 'none' };
  }
}

/**
 * Serialize AuthConfig to JSON string
 */
function serializeAuthConfig(config: AuthConfig): string {
  return JSON.stringify(config, null, 2);
}

/**
 * Create default config for auth type
 */
function createDefaultConfig(type: AuthType): AuthConfig {
  switch (type) {
    case 'none':
      return { type: 'none' };
    case 'basic':
      return { type: 'basic', username: '', password: '' };
    case 'apiKey':
      return { type: 'apiKey', key: '', location: 'header', name: 'X-API-Key' };
    case 'oauth2':
      return { type: 'oauth2', clientId: '', clientSecret: '', tokenUrl: '', scopes: '' };
    case 'clientCertificate':
      return { type: 'clientCertificate', pfx: '', password: '' };
    case 'managedIdentity':
      return { type: 'managedIdentity', audience: '', identity: '' };
    default:
      return { type: 'none' };
  }
}

export const AuthenticationField = ({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled = false,
  error,
  allowedTypes,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData: _hasRunData,
  isStale: _isStale,
  endActions,
}: AuthenticationFieldProps) => {
  const styles = useStyles();

  // Track expanded vs collapsed mode
  const [isExpanded, setIsExpanded] = useState(true);
  // Track password visibility per field
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  // Parse value into auth config
  const config = useMemo(() => parseAuthConfig(value), [value]);

  // Available auth types
  const availableTypes = allowedTypes || (Object.keys(AUTH_TYPE_LABELS) as AuthType[]);

  // Raw JSON text for collapsed mode
  const rawJsonValue = useMemo(() => {
    const stringValue = isValueSegmentArray(value)
      ? segmentsToString(value)
      : value;
    return stringValue || '{"type": "none"}';
  }, [value]);

  // Update config and notify onChange
  const updateConfig = useCallback((newConfig: AuthConfig) => {
    const serialized = serializeAuthConfig(newConfig);
    onChange([{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: serialized,
    }]);
  }, [onChange]);

  // Handle auth type change
  const handleTypeChange = useCallback((_e: unknown, data: { optionValue?: string | undefined }) => {
    const newType = (data.optionValue || 'none') as AuthType;
    updateConfig(createDefaultConfig(newType));
  }, [updateConfig]);

  // Handle field change
  const handleFieldChange = useCallback((field: string, newValue: string) => {
    const newConfig = { ...config, [field]: newValue } as AuthConfig;
    updateConfig(newConfig);
  }, [config, updateConfig]);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback((fieldId: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldId]: !prev[fieldId] }));
  }, []);

  // Handle raw JSON change in collapsed mode
  const handleRawChange = useCallback((segments: ValueSegment[]) => {
    onChange(segments);
  }, [onChange]);

  // Convert raw JSON to segments for TokenizedInput
  const rawJsonSegments: ValueSegment[] = useMemo(() => [{
    id: generateSegmentId(),
    type: ValueSegmentType.LITERAL,
    value: rawJsonValue,
  }], [rawJsonValue]);

  // Render a secure field (password-like)
  const renderSecureField = (
    fieldId: string,
    fieldLabel: string,
    fieldValue: string,
    fieldDescription?: string,
    placeholder?: string
  ) => {
    const isVisible = showPasswords[fieldId] || false;
    return (
      <div className={styles.fieldGroup}>
        <Text className={styles.fieldLabel}>{fieldLabel}</Text>
        {fieldDescription && (
          <Text className={styles.fieldDescription}>{fieldDescription}</Text>
        )}
        <div className={styles.passwordWrapper}>
          <Input
            className={styles.passwordInput}
            type={isVisible ? 'text' : 'password'}
            value={fieldValue}
            onChange={(_e, data) => handleFieldChange(fieldId, data.value)}
            disabled={disabled}
            placeholder={placeholder}
          />
          <Tooltip content={isVisible ? 'Hide' : 'Show'} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={isVisible ? <EyeOffRegular /> : <EyeRegular />}
              onClick={() => togglePasswordVisibility(fieldId)}
              disabled={disabled}
              className={styles.visibilityButton}
              aria-label={isVisible ? 'Hide password' : 'Show password'}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  // Render a text field with TokenizedInput (for expression support)
  const renderTextField = (
    fieldId: string,
    fieldLabel: string,
    fieldValue: string,
    fieldDescription?: string,
    placeholder?: string
  ) => {
    const segments: ValueSegment[] = [{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: fieldValue,
    }];

    return (
      <div className={styles.fieldGroup}>
        <Text className={styles.fieldLabel}>{fieldLabel}</Text>
        {fieldDescription && (
          <Text className={styles.fieldDescription}>{fieldDescription}</Text>
        )}
        <TokenizedInput
          name={`${name}_${fieldId}`}
          value={segments}
          onChange={(v) => handleFieldChange(fieldId, segmentsToString(v))}
          placeholder={placeholder}
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
      </div>
    );
  };

  // Render type-specific fields
  const renderTypeFields = () => {
    switch (config.type) {
      case 'none':
        return (
          <div className={styles.noAuthHint}>
            <Text>No authentication required.</Text>
          </div>
        );

      case 'basic': {
        const basicConfig = config as BasicAuthConfig;
        return (
          <div className={styles.formFields}>
            {renderTextField('username', 'Username', basicConfig.username, undefined, 'Enter username')}
            {renderSecureField('password', 'Password', basicConfig.password, undefined, 'Enter password')}
          </div>
        );
      }

      case 'apiKey': {
        const apiKeyConfig = config as ApiKeyAuthConfig;
        return (
          <div className={styles.formFields}>
            {renderSecureField('key', 'API Key', apiKeyConfig.key, undefined, 'Enter API key')}
            <div className={styles.fieldGroup}>
              <Text className={styles.fieldLabel}>Location</Text>
              <Text className={styles.fieldDescription}>Where to send the API key</Text>
              <Dropdown
                value={apiKeyConfig.location === 'header' ? 'Header' : 'Query'}
                selectedOptions={[apiKeyConfig.location]}
                onOptionSelect={(_e, data) =>
                  handleFieldChange('location', data.optionValue as ApiKeyLocation)
                }
                disabled={disabled}
              >
                <Option value="header">Header</Option>
                <Option value="query">Query Parameter</Option>
              </Dropdown>
            </div>
            {renderTextField('name', 'Parameter Name', apiKeyConfig.name, 'Name of the header or query parameter', 'X-API-Key')}
          </div>
        );
      }

      case 'oauth2': {
        const oauthConfig = config as OAuth2AuthConfig;
        return (
          <div className={styles.formFields}>
            {renderTextField('clientId', 'Client ID', oauthConfig.clientId, undefined, 'Enter client ID')}
            {renderSecureField('clientSecret', 'Client Secret', oauthConfig.clientSecret, undefined, 'Enter client secret')}
            {renderTextField('tokenUrl', 'Token URL', oauthConfig.tokenUrl, 'OAuth token endpoint URL', 'https://example.com/oauth/token')}
            {renderTextField('scopes', 'Scopes', oauthConfig.scopes, 'Space-separated list of scopes', 'read write')}
          </div>
        );
      }

      case 'clientCertificate': {
        const certConfig = config as ClientCertificateAuthConfig;
        return (
          <div className={styles.formFields}>
            {renderTextField('pfx', 'Certificate (PFX)', certConfig.pfx, 'Base64-encoded PFX file', 'Paste base64 certificate')}
            {renderSecureField('password', 'Certificate Password', certConfig.password, undefined, 'Enter certificate password')}
          </div>
        );
      }

      case 'managedIdentity': {
        const miConfig = config as ManagedIdentityAuthConfig;
        return (
          <div className={styles.formFields}>
            {renderTextField('audience', 'Audience', miConfig.audience, 'Target resource audience URL', 'https://management.azure.com/')}
            {renderTextField('identity', 'Identity (Optional)', miConfig.identity || '', 'User-assigned managed identity client ID', 'Client ID (optional)')}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <div className={styles.modeToggle}>
          {endActions}
          <Tooltip content="Visual editor" relationship="label">
            <Button
              appearance={isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<TextFieldFilled />}
              onClick={() => setIsExpanded(true)}
              disabled={disabled}
              aria-label="Visual editor"
            />
          </Tooltip>
          <Tooltip content="Edit as JSON" relationship="label">
            <Button
              appearance={!isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<CodeFilled />}
              onClick={() => setIsExpanded(false)}
              disabled={disabled}
              aria-label="Edit as JSON"
            />
          </Tooltip>
        </div>
      </div>

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      {isExpanded ? (
        // Expanded mode - visual editor
        <>
          <div className={styles.typeSelector}>
            <Dropdown
              value={AUTH_TYPE_LABELS[config.type]}
              selectedOptions={[config.type]}
              onOptionSelect={handleTypeChange}
              disabled={disabled}
              aria-label="Authentication type"
            >
              {availableTypes.map((type) => (
                <Option key={type} value={type}>
                  {AUTH_TYPE_LABELS[type]}
                </Option>
              ))}
            </Dropdown>
          </div>
          {renderTypeFields()}
        </>
      ) : (
        // Collapsed mode - raw JSON editor
        <TokenizedInput
          name={name}
          value={rawJsonSegments}
          onChange={handleRawChange}
          placeholder='{"type": "none"}'
          multiline
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
      )}

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

AuthenticationField.displayName = 'AuthenticationField';
